import React from 'react'
import { useTranslation } from 'react-i18next'
import { useOrgNavigate } from '@/hooks/use-app-navigate'
import { Loader2 } from 'lucide-react'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { ProcessorDetails, useDeleteProcessors } from '@/lib/api/processors'
import { useToast } from '@/components/ui/use-toast'

interface ProcessorDeleteDialogProps {
    processor: ProcessorDetails
    open: boolean
    onOpenChange: (open: boolean) => void
}

export const ProcessorDeleteDialog = ({
    processor,
    open,
    onOpenChange,
}: ProcessorDeleteDialogProps) => {
    const { t } = useTranslation('processors')
    const { toast } = useToast()
    const { orgRoutes, navigate } = useOrgNavigate()
    const deleteProcessors = useDeleteProcessors()

    const handleDelete = async () => {
        if (!processor?.id) return

        try {
            await deleteProcessors.mutateAsync({
                processorIds: [processor.id],
            })

            toast({
                title: t('deleteSuccess'),
                description: t('processorDeleted'),
            })

            navigate(`${orgRoutes.PAYMENT_FLOWS()}?tab=processors`)
        } catch (error) {
            toast({
                title: t('deleteError'),
                description: t('processorDeleteFailed'),
                variant: 'destructive',
            })
        }
    }

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('deleteProcessor')}</AlertDialogTitle>
                    <AlertDialogDescription>
                        {t('deleteProcessorConfirmation', { name: processor.name })}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('cancel')}
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={deleteProcessors.isPending}
                    >
                        {deleteProcessors.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {t('delete')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

